import { Map as MapLibreMap } from 'maplibre-gl';
import 'maplibre-gl/dist/maplibre-gl.css';
import * as satellite from 'satellite.js';
import { openCache, cacheGet, cacheSet } from './cache.js';
import { sunGeocentric, moonGeocentric, equatorialToEcf } from './celestial.js';
import { createSpaceView } from './space-view.js';
import { computeVisiblePasses } from './iss-passes.js';

const CONFIG = window.GEV_CONFIG || {};
const API_BASE = (CONFIG.apiBase || '').replace(/\/$/, '');
const FALLBACK_CENTER = { lat: 51.2069, lng: -1.4816 }; // Andover, UK — used if geolocation is denied
const REGIONAL_RADIUS_DEG = 20;
const AIRCRAFT_POLL_MS = 15_000;
const SHIP_POLL_MS = 12_000;
const WEATHER_POLL_MS = 10 * 60_000; // matches the worker's 10-min edge cache
const SATELLITE_TLE_POLL_MS = 30 * 60_000; // matches the worker's 30-min edge cache
const SATELLITE_PROPAGATE_MS = 3_000; // re-propagate from cached TLEs — no network call
const ISS_NORAD_ID = 25544;

// OpenFreeMap: free OpenStreetMap-based vector tiles, no API key or account
// needed. MapLibre's globe projection morphs into a flat street-level map as
// you zoom in, so this is the same map for the world view and close-up zoom.
// "dark" (not the default "liberty") keeps the map from washing out the HUD.
const MAP_STYLE_URL = 'https://tiles.openfreemap.org/styles/dark';
const SPACE_VIEW_ZOOM_THRESHOLD = 1.3; // below this, fade from the map into the space view

const WEATHER_COLORS = {
  clear: '#ffd166',
  cloudy: '#9fb3c8',
  fog: '#b8c4c8',
  rain: '#4fa8ff',
  snow: '#eaf6ff',
  storm: '#ff4d6d',
};

// Traffic density is computed from aircraft+ship positions already on the
// map — no new data source. Binned into a coarse grid of tiered markers
// rather than a true heatmap layer, keeping it consistent with the other
// point layers (same click-to-detail handling, no extra render path).
const TRAFFIC_GRID_DEG = 12;
const TRAFFIC_THRESHOLDS = { medium: 3, high: 8 };
const TRAFFIC_TIER_COLORS = { low: '#5cff8a', medium: '#ffd166', high: '#ff4d6d' };
const TRAFFIC_TIER_RADIUS = { low: 16, medium: 24, high: 34 }; // pixels

const state = {
  mode: 'regional', // 'regional' | 'global'
  center: FALLBACK_CENTER,
  aircraft: [],
  ships: [],
  weather: [],
  satelliteTles: [],
  satellitePositions: [],
  deepSpace: [],
  aircraftAsOf: null,
  shipsAsOf: null,
  weatherAsOf: null,
  satellitesAsOf: null,
  aircraftLive: false,
  shipsLive: false,
  weatherLive: false,
  satellitesLive: false,
  showAircraft: true,
  showShips: true,
  showWeather: true,
  showSatellites: true,
  showTelescopes: true,
  showTraffic: false, // opt-in — a density overlay on top of the point layers
  selected: null,
};

// ---------------------------------------------------------------------------
// UI helpers
// ---------------------------------------------------------------------------
const $ = (sel) => document.querySelector(sel);
const statusEl = $('#status-line');
const aircraftCountEl = $('#aircraft-count');
const shipCountEl = $('#ship-count');
const weatherCountEl = $('#weather-count');
const satelliteCountEl = $('#satellite-count');
const telescopeCountEl = $('#telescope-count');
const offlineBannerEl = $('#offline-banner');
const deepSpaceNoteEl = $('#deep-space-note');
const infoPanelEl = $('#info-panel');
const infoContentEl = $('#info-content');
const modeBtn = $('#mode-toggle');
const layerAircraftBtn = $('#layer-aircraft');
const layerShipsBtn = $('#layer-ships');
const layerWeatherBtn = $('#layer-weather');
const layerSatellitesBtn = $('#layer-satellites');
const layerTelescopesBtn = $('#layer-telescopes');
const layerTrafficBtn = $('#layer-traffic');
const issPassesBtn = $('#iss-passes');
const recenterBtn = $('#recenter');

function fmtTime(ms) {
  if (!ms) return '—';
  return new Date(ms).toLocaleTimeString();
}

function setStatus(text) {
  statusEl.textContent = text;
}

function renderCounts() {
  aircraftCountEl.textContent = `${state.aircraft.length}`;
  shipCountEl.textContent = `${state.ships.length}`;
  weatherCountEl.textContent = `${state.weather.length}`;
  satelliteCountEl.textContent = `${state.satellitePositions.filter((s) => s.kind !== 'telescope').length}`;
  telescopeCountEl.textContent = `${state.satellitePositions.filter((s) => s.kind === 'telescope').length}`;
}

function renderDeepSpaceNote() {
  if (state.showTelescopes && state.deepSpace.length) {
    deepSpaceNoteEl.textContent = state.deepSpace.map((d) => `🔭 ${d.name} — ${d.note}`).join(' ');
    deepSpaceNoteEl.classList.add('visible');
  } else {
    deepSpaceNoteEl.classList.remove('visible');
  }
}

function bboxFor(center, radiusDeg) {
  return {
    lamin: Math.max(-90, center.lat - radiusDeg),
    lamax: Math.min(90, center.lat + radiusDeg),
    lomin: Math.max(-180, center.lng - radiusDeg),
    lomax: Math.min(180, center.lng + radiusDeg),
  };
}

// ---------------------------------------------------------------------------
// Map setup — MapLibre GL JS in globe projection. The same engine handles
// both the orbit-globe world view and street-level zoom (it morphs from
// globe to flat mercator as you zoom in), so there's no separate map to
// keep in sync.
// ---------------------------------------------------------------------------
let mapReady = false;
const POINT_LAYER_IDS = ['traffic', 'weather', 'aircraft', 'ships', 'satellites', 'telescopes'];

const map = new MapLibreMap({
  container: 'globe-container',
  style: MAP_STYLE_URL,
  center: [state.center.lng, state.center.lat],
  zoom: 2,
});

function emptyFC() {
  return { type: 'FeatureCollection', features: [] };
}

function geoPoint(lng, lat, properties) {
  return { type: 'Feature', geometry: { type: 'Point', coordinates: [lng, lat] }, properties };
}

map.on('load', () => {
  map.setProjection({ type: 'globe' });

  // 3D building extrusions once you're zoomed in close enough for them to be
  // legible — OpenMapTiles' building layer carries real height data.
  map.addLayer({
    id: 'buildings-3d',
    type: 'fill-extrusion',
    source: 'openmaptiles',
    'source-layer': 'building',
    minzoom: 14,
    filter: ['match', ['geometry-type'], ['MultiPolygon', 'Polygon'], true, false],
    paint: {
      'fill-extrusion-color': '#1c2733',
      'fill-extrusion-height': ['coalesce', ['get', 'render_height'], 15],
      'fill-extrusion-base': ['coalesce', ['get', 'render_min_height'], 0],
      'fill-extrusion-opacity': 0.85,
    },
  });

  // Added in back-to-front order: traffic (a background density glow) first,
  // so later layers draw on top of it.
  for (const layerId of POINT_LAYER_IDS) {
    map.addSource(layerId, { type: 'geojson', data: emptyFC() });
    map.addLayer({
      id: layerId,
      type: 'circle',
      source: layerId,
      paint: {
        'circle-radius': ['get', 'radius'],
        'circle-color': ['get', 'color'],
        'circle-opacity': ['get', 'opacity'],
        'circle-stroke-width': 0.5,
        'circle-stroke-color': 'rgba(0,0,0,0.5)',
      },
    });
    map.on('click', layerId, (e) => handleMapClick(layerId, e));
    map.on('mouseenter', layerId, () => (map.getCanvas().style.cursor = 'pointer'));
    map.on('mouseleave', layerId, () => (map.getCanvas().style.cursor = ''));
  }

  mapReady = true;
  mergePoints();

  symbolLayerIds = map.getStyle().layers.filter((l) => l.type === 'symbol').map((l) => l.id);
  updateSpaceViewVisibility();
});

window.addEventListener('resize', () => map.resize());

// ---------------------------------------------------------------------------
// Space view — fades in once you zoom out past normal planet view, showing
// Earth/Sun/Moon/satellites with no map labels. See space-view.js for why
// this is a separate overlay canvas rather than a MapLibre custom layer.
// ---------------------------------------------------------------------------
const spaceView = createSpaceView(document.getElementById('space-canvas'));
let symbolLayerIds = [];
let spaceActive = false;

function updateSpaceViewVisibility() {
  if (!mapReady) return;
  const shouldShow = map.getZoom() < SPACE_VIEW_ZOOM_THRESHOLD;
  if (shouldShow === spaceActive) return;
  spaceActive = shouldShow;
  document.body.classList.toggle('space-active', spaceActive);
  for (const id of symbolLayerIds) {
    map.setLayoutProperty(id, 'visibility', spaceActive ? 'none' : 'visible');
  }
  if (spaceActive) {
    spaceView.start();
    updateSpaceView(new Date(), satellite.gstime(new Date()));
  } else {
    spaceView.stop();
  }
}

map.on('zoom', updateSpaceViewVisibility);

function handleMapClick(layerId, e) {
  const id = e.features[0].properties.id;
  let html = null;
  if (layerId === 'aircraft') {
    const a = state.aircraft.find((x) => x.icao24 === id);
    if (a) html = aircraftDetailHtml(a);
  } else if (layerId === 'ships') {
    const s = state.ships.find((x) => String(x.mmsi) === id);
    if (s) html = shipDetailHtml(s);
  } else if (layerId === 'weather') {
    const w = state.weather.find((x) => x.name === id);
    if (w) html = weatherDetailHtml(w);
  } else if (layerId === 'satellites' || layerId === 'telescopes') {
    const s = state.satellitePositions.find((x) => String(x.noradId) === id);
    if (s) html = satelliteDetailHtml(s);
  } else if (layerId === 'traffic') {
    const cell = trafficCells().find((c) => c.key === id);
    if (cell) html = trafficDetailHtml(cell, trafficTier(cell.count));
  }
  if (html) selectEntity({ detailHtml: html });
}

function selectEntity(point) {
  state.selected = point;
  if (!point) {
    infoPanelEl.classList.remove('visible');
    return;
  }
  infoContentEl.innerHTML = point.detailHtml;
  infoPanelEl.classList.add('visible');
}

function mergePoints() {
  if (!mapReady) return;
  map.getSource('aircraft').setData(state.showAircraft ? aircraftToFC() : emptyFC());
  map.getSource('ships').setData(state.showShips ? shipsToFC() : emptyFC());
  map.getSource('weather').setData(state.showWeather ? weatherToFC() : emptyFC());
  map.getSource('satellites').setData(state.showSatellites ? satellitesToFC() : emptyFC());
  map.getSource('telescopes').setData(state.showTelescopes ? telescopesToFC() : emptyFC());
  map.getSource('traffic').setData(state.showTraffic ? trafficToFC() : emptyFC());
}

function aircraftToFC() {
  return {
    type: 'FeatureCollection',
    features: state.aircraft
      .filter((a) => a.latitude != null && a.longitude != null)
      .map((a) =>
        geoPoint(a.longitude, a.latitude, {
          id: a.icao24,
          color: a.onGround ? '#2f7a86' : '#00e5ff',
          radius: 5,
          opacity: 0.9,
        }),
      ),
  };
}

function shipsToFC() {
  return {
    type: 'FeatureCollection',
    features: state.ships
      .filter((s) => s.lat != null && s.lon != null)
      .map((s) => geoPoint(s.lon, s.lat, { id: String(s.mmsi), color: '#ffb648', radius: 5, opacity: 0.9 })),
  };
}

function weatherToFC() {
  return {
    type: 'FeatureCollection',
    features: state.weather.map((w) =>
      geoPoint(w.lon, w.lat, {
        id: w.name,
        color: WEATHER_COLORS[w.severity] || WEATHER_COLORS.clear,
        radius: 7,
        opacity: 0.55,
      }),
    ),
  };
}

function satellitesToFC() {
  return {
    type: 'FeatureCollection',
    features: state.satellitePositions
      .filter((s) => s.kind !== 'telescope')
      .map((s) => geoPoint(s.lon, s.lat, { id: String(s.noradId), color: '#7dffb2', radius: 6, opacity: 0.9 })),
  };
}

function telescopesToFC() {
  return {
    type: 'FeatureCollection',
    features: state.satellitePositions
      .filter((s) => s.kind === 'telescope')
      .map((s) => geoPoint(s.lon, s.lat, { id: String(s.noradId), color: '#c792ff', radius: 6, opacity: 0.9 })),
  };
}

function trafficToFC() {
  return {
    type: 'FeatureCollection',
    features: trafficCells().map((c) => {
      const tier = trafficTier(c.count);
      return geoPoint(c.lng, c.lat, {
        id: c.key,
        color: TRAFFIC_TIER_COLORS[tier],
        radius: TRAFFIC_TIER_RADIUS[tier],
        opacity: 0.35,
      });
    }),
  };
}

function trafficCells() {
  const grid = new Map();
  const add = (lat, lon) => {
    if (lat == null || lon == null) return;
    const key = `${Math.floor(lat / TRAFFIC_GRID_DEG)}:${Math.floor(lon / TRAFFIC_GRID_DEG)}`;
    const cell = grid.get(key) || { key, count: 0, latSum: 0, lonSum: 0 };
    cell.count += 1;
    cell.latSum += lat;
    cell.lonSum += lon;
    grid.set(key, cell);
  };
  for (const a of state.aircraft) add(a.latitude, a.longitude);
  for (const s of state.ships) add(s.lat, s.lon);
  return [...grid.values()].map((c) => ({
    key: c.key,
    lat: c.latSum / c.count,
    lng: c.lonSum / c.count,
    count: c.count,
  }));
}

function trafficTier(count) {
  if (count >= TRAFFIC_THRESHOLDS.high) return 'high';
  if (count >= TRAFFIC_THRESHOLDS.medium) return 'medium';
  return 'low';
}

function trafficDetailHtml(cell, tier) {
  return `
    <div class="entity-title">🔥 ${tier[0].toUpperCase()}${tier.slice(1)} traffic</div>
    <div class="entity-row"><span>Tracked objects</span><span>${cell.count}</span></div>
  `;
}

function aircraftDetailHtml(a) {
  return `
    <div class="entity-title">✈ ${a.callsign ? a.callsign.trim() : 'Unknown callsign'}</div>
    <div class="entity-row"><span>ICAO24</span><span>${a.icao24}</span></div>
    <div class="entity-row"><span>Origin</span><span>${a.originCountry || '—'}</span></div>
    <div class="entity-row"><span>Altitude</span><span>${
      a.baroAltitude ? Math.round(a.baroAltitude * 3.28084) + ' ft' : '—'
    }</span></div>
    <div class="entity-row"><span>Speed</span><span>${
      a.velocity ? Math.round(a.velocity * 1.94384) + ' kt' : '—'
    }</span></div>
    <div class="entity-row"><span>Heading</span><span>${
      a.trueTrack != null ? Math.round(a.trueTrack) + '°' : '—'
    }</span></div>
    <div class="entity-row"><span>Status</span><span>${a.onGround ? 'On ground' : 'Airborne'}</span></div>
  `;
}

function shipDetailHtml(s) {
  return `
    <div class="entity-title">🚢 ${s.name || 'Unknown vessel'}</div>
    <div class="entity-row"><span>MMSI</span><span>${s.mmsi}</span></div>
    <div class="entity-row"><span>Speed</span><span>${(s.sog ?? 0).toFixed(1)} kn</span></div>
    <div class="entity-row"><span>Course</span><span>${
      s.cog != null ? Math.round(s.cog) + '°' : '—'
    }</span></div>
    <div class="entity-row"><span>Heading</span><span>${
      s.heading != null && s.heading !== 511 ? Math.round(s.heading) + '°' : '—'
    }</span></div>
  `;
}

function weatherDetailHtml(w) {
  return `
    <div class="entity-title">☁ ${w.name}</div>
    <div class="entity-row"><span>Condition</span><span>${w.condition}</span></div>
    <div class="entity-row"><span>Temp</span><span>${w.tempC != null ? Math.round(w.tempC) + '°C' : '—'}</span></div>
    <div class="entity-row"><span>Wind</span><span>${w.windKph != null ? Math.round(w.windKph) + ' km/h' : '—'}</span></div>
    <div class="entity-row"><span>Humidity</span><span>${w.humidity != null ? w.humidity + '%' : '—'}</span></div>
  `;
}

function fmtPassTime(date) {
  return date.toLocaleString([], { weekday: 'short', hour: '2-digit', minute: '2-digit' });
}

function issPassesHtml(passes) {
  if (!passes.length) {
    return `
      <div class="entity-title">🛰 ISS visible passes</div>
      <div class="entity-row"><span>No visible passes</span><span>next 10 days</span></div>
    `;
  }
  const rows = passes
    .map(
      (p) => `
    <div class="entity-row">
      <span>${fmtPassTime(p.start.time)}</span>
      <span>${Math.round(p.max.elevationDeg)}° max · ${p.durationSec}s · ${p.start.compass}→${p.end.compass}</span>
    </div>`,
    )
    .join('');
  return `<div class="entity-title">🛰 ISS visible passes (next 10 days)</div>${rows}`;
}

function showIssPasses() {
  const issTle = state.satelliteTles.find((s) => s.noradId === ISS_NORAD_ID);
  if (!issTle) {
    selectEntity({
      detailHtml: `
        <div class="entity-title">🛰 ISS visible passes</div>
        <div class="entity-row"><span>ISS data not loaded yet</span><span>—</span></div>
      `,
    });
    return;
  }
  selectEntity({
    detailHtml: `
      <div class="entity-title">🛰 ISS visible passes</div>
      <div class="entity-row"><span>Computing…</span><span>—</span></div>
    `,
  });
  // Double-defer so the "Computing…" state actually paints before the
  // ~10-day pass scan runs synchronously on the main thread.
  requestAnimationFrame(() => {
    setTimeout(() => {
      const passes = computeVisiblePasses(issTle.tleLine1, issTle.tleLine2, state.center);
      selectEntity({ detailHtml: issPassesHtml(passes) });
    }, 0);
  });
}

function satelliteDetailHtml(s) {
  return `
    <div class="entity-title">${s.kind === 'telescope' ? '🔭' : '🛰'} ${s.name}</div>
    <div class="entity-row"><span>NORAD ID</span><span>${s.noradId}</span></div>
    <div class="entity-row"><span>Altitude</span><span>${Math.round(s.heightKm)} km</span></div>
    <div class="entity-row"><span>Velocity</span><span>${
      s.velocityKms != null ? s.velocityKms.toFixed(2) + ' km/s' : '—'
    }</span></div>
  `;
}

// ---------------------------------------------------------------------------
// Data fetching (with offline-aware fallback to last cached snapshot)
// ---------------------------------------------------------------------------
async function fetchJson(path, params) {
  if (!API_BASE) throw new Error('no_api_configured');
  const url = new URL(API_BASE + path);
  Object.entries(params || {}).forEach(([k, v]) => url.searchParams.set(k, v));
  const res = await fetch(url.toString(), { cache: 'no-store' });
  if (!res.ok) throw new Error(`http_${res.status}`);
  return res.json();
}

async function refreshAircraft() {
  const bbox = state.mode === 'regional' ? bboxFor(state.center, REGIONAL_RADIUS_DEG) : {};
  try {
    const data = await fetchJson('/aircraft', bbox);
    state.aircraft = data.aircraft || [];
    state.aircraftAsOf = data.asOf;
    state.aircraftLive = true;
    await cacheSet('aircraft', { aircraft: state.aircraft, asOf: state.aircraftAsOf });
  } catch (err) {
    state.aircraftLive = false;
    const cached = await cacheGet('aircraft');
    if (cached) {
      state.aircraft = cached.aircraft;
      state.aircraftAsOf = cached.asOf;
    }
  }
  mergePoints();
  renderCounts();
  updateFreshnessUi();
}

async function refreshShips() {
  const bbox = state.mode === 'regional' ? bboxFor(state.center, REGIONAL_RADIUS_DEG) : {};
  try {
    const data = await fetchJson('/ships', bbox);
    let ships = data.ships || [];
    if (state.mode === 'regional') {
      const b = bboxFor(state.center, REGIONAL_RADIUS_DEG);
      ships = ships.filter((s) => s.lat >= b.lamin && s.lat <= b.lamax && s.lon >= b.lomin && s.lon <= b.lomax);
    }
    state.ships = ships;
    state.shipsAsOf = data.asOf;
    state.shipsLive = data.connected;
    await cacheSet('ships', { ships: state.ships, asOf: state.shipsAsOf });
  } catch (err) {
    state.shipsLive = false;
    const cached = await cacheGet('ships');
    if (cached) {
      state.ships = cached.ships;
      state.shipsAsOf = cached.asOf;
    }
  }
  mergePoints();
  renderCounts();
  updateFreshnessUi();
}

async function refreshWeather() {
  try {
    const data = await fetchJson('/weather', {});
    state.weather = data.weather || [];
    state.weatherAsOf = data.asOf;
    state.weatherLive = true;
    await cacheSet('weather', { weather: state.weather, asOf: state.weatherAsOf });
  } catch (err) {
    state.weatherLive = false;
    const cached = await cacheGet('weather');
    if (cached) {
      state.weather = cached.weather;
      state.weatherAsOf = cached.asOf;
    }
  }
  mergePoints();
  renderCounts();
  updateFreshnessUi();
}

function computeSatellitePositions() {
  const now = new Date();
  const gmst = satellite.gstime(now);
  state.satellitePositions = state.satelliteTles
    .map((sat) => {
      try {
        const satrec = satellite.twoline2satrec(sat.tleLine1, sat.tleLine2);
        const pv = satellite.propagate(satrec, now);
        if (!pv.position) return null;
        const geo = satellite.eciToGeodetic(pv.position, gmst);
        const v = pv.velocity;
        return {
          name: sat.name,
          kind: sat.kind,
          noradId: sat.noradId,
          lat: satellite.degreesLat(geo.latitude),
          lon: satellite.degreesLong(geo.longitude),
          heightKm: geo.height,
          velocityKms: v ? Math.sqrt(v.x * v.x + v.y * v.y + v.z * v.z) : null,
          ecf: satellite.eciToEcf(pv.position, gmst),
        };
      } catch (err) {
        return null;
      }
    })
    .filter(Boolean);
  mergePoints();
  renderCounts();
  updateSpaceView(now, gmst);
}

function updateSpaceView(now, gmst) {
  if (!spaceView) return;
  const sun = sunGeocentric(now);
  const moon = moonGeocentric(now);
  spaceView.update({
    sunEcf: equatorialToEcf(sun.ra, sun.dec, sun.distKm, gmst),
    moonEcf: equatorialToEcf(moon.ra, moon.dec, moon.distKm, gmst),
    satellites: state.satellitePositions,
  });
}

async function refreshSatelliteTles() {
  try {
    const data = await fetchJson('/satellites', {});
    state.satelliteTles = data.satellites || [];
    state.deepSpace = data.deepSpace || [];
    state.satellitesAsOf = data.asOf;
    state.satellitesLive = true;
    await cacheSet('satellites', {
      satellites: state.satelliteTles,
      deepSpace: state.deepSpace,
      asOf: state.satellitesAsOf,
    });
  } catch (err) {
    state.satellitesLive = false;
    const cached = await cacheGet('satellites');
    if (cached) {
      state.satelliteTles = cached.satellites;
      state.deepSpace = cached.deepSpace || [];
      state.satellitesAsOf = cached.asOf;
    }
  }
  computeSatellitePositions();
  renderDeepSpaceNote();
  updateFreshnessUi();
}

function updateFreshnessUi() {
  const online =
    navigator.onLine && (state.aircraftLive || state.shipsLive || state.weatherLive || state.satellitesLive);
  if (!online) {
    const newest = Math.max(
      state.aircraftAsOf || 0,
      state.shipsAsOf || 0,
      state.weatherAsOf || 0,
      state.satellitesAsOf || 0,
    );
    offlineBannerEl.textContent = newest
      ? `OFFLINE — showing last known positions as of ${fmtTime(newest)}`
      : 'OFFLINE — no cached data yet';
    offlineBannerEl.classList.add('visible');
  } else {
    offlineBannerEl.classList.remove('visible');
  }
  setStatus(
    `✈ ${fmtTime(state.aircraftAsOf)} ${state.aircraftLive ? '●' : '○'}  ` +
      `🚢 ${fmtTime(state.shipsAsOf)} ${state.shipsLive ? '●' : '○'}  ` +
      `☁ ${fmtTime(state.weatherAsOf)} ${state.weatherLive ? '●' : '○'}  ` +
      `🛰 ${fmtTime(state.satellitesAsOf)} ${state.satellitesLive ? '●' : '○'}`,
  );
}

// ---------------------------------------------------------------------------
// Controls
// ---------------------------------------------------------------------------
modeBtn.addEventListener('click', () => {
  state.mode = state.mode === 'regional' ? 'global' : 'regional';
  modeBtn.textContent = state.mode === 'regional' ? 'REGIONAL' : 'GLOBAL';
  refreshAircraft();
  refreshShips();
});

layerAircraftBtn.addEventListener('click', () => {
  state.showAircraft = !state.showAircraft;
  layerAircraftBtn.classList.toggle('active', state.showAircraft);
  mergePoints();
});

layerShipsBtn.addEventListener('click', () => {
  state.showShips = !state.showShips;
  layerShipsBtn.classList.toggle('active', state.showShips);
  mergePoints();
});

layerWeatherBtn.addEventListener('click', () => {
  state.showWeather = !state.showWeather;
  layerWeatherBtn.classList.toggle('active', state.showWeather);
  mergePoints();
});

layerSatellitesBtn.addEventListener('click', () => {
  state.showSatellites = !state.showSatellites;
  layerSatellitesBtn.classList.toggle('active', state.showSatellites);
  mergePoints();
});

layerTelescopesBtn.addEventListener('click', () => {
  state.showTelescopes = !state.showTelescopes;
  layerTelescopesBtn.classList.toggle('active', state.showTelescopes);
  mergePoints();
  renderDeepSpaceNote();
});

layerTrafficBtn.addEventListener('click', () => {
  state.showTraffic = !state.showTraffic;
  layerTrafficBtn.classList.toggle('active', state.showTraffic);
  mergePoints();
});

issPassesBtn.addEventListener('click', showIssPasses);

recenterBtn.addEventListener('click', () => {
  map.flyTo({ center: [state.center.lng, state.center.lat], zoom: 2, duration: 800 });
});

document.getElementById('close-info').addEventListener('click', () => selectEntity(null));

window.addEventListener('online', () => {
  refreshAircraft();
  refreshShips();
  refreshWeather();
  refreshSatelliteTles();
});
window.addEventListener('offline', updateFreshnessUi);

// ---------------------------------------------------------------------------
// Boot
// ---------------------------------------------------------------------------
async function boot() {
  await openCache();

  // Paint instantly from cache while live data loads.
  const [cachedAircraft, cachedShips, cachedWeather, cachedSatellites] = await Promise.all([
    cacheGet('aircraft'),
    cacheGet('ships'),
    cacheGet('weather'),
    cacheGet('satellites'),
  ]);
  if (cachedAircraft) {
    state.aircraft = cachedAircraft.aircraft;
    state.aircraftAsOf = cachedAircraft.asOf;
  }
  if (cachedShips) {
    state.ships = cachedShips.ships;
    state.shipsAsOf = cachedShips.asOf;
  }
  if (cachedWeather) {
    state.weather = cachedWeather.weather;
    state.weatherAsOf = cachedWeather.asOf;
  }
  if (cachedSatellites) {
    state.satelliteTles = cachedSatellites.satellites;
    state.deepSpace = cachedSatellites.deepSpace || [];
    state.satellitesAsOf = cachedSatellites.asOf;
    computeSatellitePositions();
  }
  mergePoints();
  renderCounts();
  renderDeepSpaceNote();
  updateFreshnessUi();

  if (navigator.geolocation) {
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        state.center = { lat: pos.coords.latitude, lng: pos.coords.longitude };
        map.jumpTo({ center: [state.center.lng, state.center.lat], zoom: 2 });
        refreshAircraft();
        refreshShips();
      },
      () => {
        // denied/unavailable — fall back silently to FALLBACK_CENTER
        refreshAircraft();
        refreshShips();
      },
      { timeout: 6000 },
    );
  } else {
    refreshAircraft();
    refreshShips();
  }
  refreshWeather();
  refreshSatelliteTles();

  setInterval(refreshAircraft, AIRCRAFT_POLL_MS);
  setInterval(refreshShips, SHIP_POLL_MS);
  setInterval(refreshWeather, WEATHER_POLL_MS);
  setInterval(refreshSatelliteTles, SATELLITE_TLE_POLL_MS);
  setInterval(computeSatellitePositions, SATELLITE_PROPAGATE_MS);
}

boot();

if ('serviceWorker' in navigator) {
  window.addEventListener('load', () => {
    navigator.serviceWorker.register('sw.js').catch((err) => console.error('SW register failed', err));
  });
}
